/**
 * Etiquetas legibles para IDs de fibra en UI (trazabilidad, mapa, PDF).
 */

import type { Cable } from '@/types/fibers'
import { parseFiberId } from '@/types/fibers'
import { cableLabelShort, type CableLike } from '@/utils/cableDisplayLabel'

export type FiberIdDisplay = {
  /** Texto principal para el usuario */
  primary: string
  /** ID completo para soporte / copiar */
  technical: string
}

function cableById(cables: Cable[], id: string): Cable | undefined {
  return cables.find((c) => c.id === id)
}

/**
 * Interpreta `fiberId` (formato tubo|color, UUID, prefijos internos) y devuelve texto legible + técnico.
 */
export function describeFiberIdForUi(fiberId: string, cables: Cable[]): FiberIdDisplay {
  const technical = fiberId || ''
  if (!fiberId?.trim()) return { primary: '—', technical: '' }

  if (fiberId.startsWith('trace-')) {
    return { primary: 'Trazado de ruta (sin fibra de drop específica)', technical }
  }

  const parts = fiberId.split('|')
  if (parts.length === 3) {
    try {
      const p = parseFiberId(fiberId)
      const cab = cableById(cables, p.cableOrNodeId)
      const cablePart = cab
        ? cableLabelShort(cab as CableLike)
        : p.cableOrNodeId.length > 20
          ? `Cable …${p.cableOrNodeId.slice(0, 8)}…`
          : p.cableOrNodeId
      const tubo = p.buffer != null && p.buffer !== 'SB' ? ` · tubo ${p.buffer}` : ''
      const primary = `${cablePart}${tubo} · hilo (${p.color})`
      return { primary, technical }
    } catch {
      /* continuar */
    }
  }

  const short =
    fiberId.length > 48 ? `${fiberId.slice(0, 10)}…${fiberId.slice(-8)}` : fiberId
  return { primary: `Fibra / enlace: ${short}`, technical }
}
